import { useState } from "react";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { toast } from "react-toastify";
import { CloseIcon } from "../../assets/accountIcons";
import { submitSupportEmail } from "../../features/store/supportSlice";
import { useAppDispatch } from "../../hooks/hooks";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";

interface EmailFormData {
  email: string;
  subject: string;
  body: string; // Changed from 'message' to match Laravel backend
}

interface EmailModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const EmailSupportModal = ({ isOpen, onClose }: EmailModalProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useAppDispatch();
  const user = useSelector((state: RootState) => state.auth.user);

  const schema = yup
    .object({
      email: yup
        .string()
        .email("Please enter a valid email")
        .required("Email is required")
        .label("Email"),
      subject: yup
        .string()
        .required("Please provide a subject")
        .min(5)
        .label("Subject"),
      body: yup
        .string()
        .required("Message body cannot be empty")
        .min(10)
        .label("Message Body"),
    })
    .required();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<EmailFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      email: user?.email || "",
      subject: "",
      body: "",
    },
  });

  const onSubmit = async (data: EmailFormData) => {
    setIsLoading(true);
    await dispatch(submitSupportEmail(data))
      .unwrap()
      .then((res) => {
        toast.success(
          res?.message || "Your message has been sent successfully.",
        );
        reset();
        onClose();
      })
      .catch((err) => {
        toast.error(
          err?.message || "Failed to send message. Please try again.",
        );
      })
      .finally(() => setIsLoading(false));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-BG-dark w-full max-w-md rounded-xl p-6 shadow-xl relative animate-in fade-in zoom-in duration-200">
        <div className="flex justify-between items-center mb-6">
          <h2 className="lt-xl-semibold-pry-900 dark:text-white">
            Email Support
          </h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-main-light rounded-full transition-colors"
          >
            <CloseIcon />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="flex flex-col gap-1">
            <label className="satoshi-sm-bold-main-light dark:text-white ml-1">
              Your Email Address
            </label>
            <input
              {...register("email")}
              type="email"
              placeholder="name@example.com"
              className="w-full h-11 rounded-lg p-3 border border-inputBorder focus:outline-none focus:border-pry-500 dark:bg-transparent dark:text-white"
            />
            {errors.email && (
              <span className="text-red-500 text-xs ml-1">
                {errors.email.message}
              </span>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <label className="satoshi-sm-bold-main-light dark:text-white ml-1">
              Subject
            </label>
            <input
              {...register("subject")}
              placeholder="e.g., Payment issue"
              className="w-full h-11 rounded-lg p-3 border border-inputBorder focus:outline-none focus:border-pry-500 dark:bg-transparent dark:text-white"
            />
            {errors.subject && (
              <span className="text-red-500 text-xs ml-1">
                {errors.subject.message}
              </span>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <label className="satoshi-sm-bold-main-light dark:text-white ml-1">
              Message
            </label>
            <textarea
              {...register("body")}
              placeholder="Tell us more about your inquiry..."
              rows={4}
              className="w-full rounded-lg p-3 border border-inputBorder focus:outline-none focus:border-pry-500 dark:bg-transparent dark:text-white resize-none"
            />
            {errors.body && (
              <span className="text-red-500 text-xs ml-1">
                {errors.body.message}
              </span>
            )}
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-lg px-4 py-2 border border-border-light dark:border-main-light satoshi-sm-bold-main-light dark:text-white hover:bg-gray-50 dark:hover:bg-main-light transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-[2] rounded-lg px-4 py-2 bg-gradient-to-b from-pry-500 to-pry-600 
               hover:from-pry-300 hover:to-pry-400 
               text-white satoshi-sm-bold-white 
               disabled:opacity-70 flex items-center justify-center gap-2 
               transition-all duration-200 active:scale-[0.98]"
            >
              {isLoading ? "Sending..." : "Send Message"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EmailSupportModal;
